import React, { FC } from 'react';
import { colors, type ColorToken } from '@puzzlio/tokens';
import { cn } from './cn';
import { ICONS, isStroked, type IconName, type IconPaint, type IconShape } from './spec/icons';

/*
  The icon set, built from spec/icons.ts.

  The spec is path data as plain values, so the React Native app draws the same artwork rather than a
  second transcription of it. That's an implementation detail: from the outside these are ordinary
  components, and nothing beyond this file needs to know where the shapes come from.

  Adding an icon means adding it to the spec — its paths and the classes the web draws it with — and
  a component appears here for free.
*/

export type SvgIcon = FC<{ className?: string }>;

/**
 * A spec fill as a colour.
 *
 * The spec names brand tokens; those resolve straight to their hex from @puzzlio/tokens, which is
 * where the palette is defined and where the CSS variables are generated from. Going through
 * `var(--token)` would only ask the browser to look up a value we already have. The tradeoff is that
 * these are baked at build time, so a *runtime* theme swap wouldn't repaint them — there isn't one,
 * and if there ever is, this is the line to revisit.
 */
function colourFor(fill: IconPaint['fill']): string | undefined {
  if (!fill) return undefined;
  return fill.startsWith('#') ? fill : (colors[fill as ColorToken] ?? fill);
}

/**
 * Note what is deliberately *not* set: `fill="none"` on the element. An icon whose paths carry no
 * fill and whose spec names no fill class would then render nothing at all — which is how the
 * highlighter and the arrow silently disappeared once. Without it they fall back to SVG's default
 * black: still wrong if a colour was intended, but wrong in a way you can see.
 */
function makeSvgIcon(name: IconName): SvgIcon {
  const spec = ICONS[name];
  /*
    Clip ids must be unique in a document and an icon can appear many times on a page, so they carry
    the icon's name as well as the shape's position.
  */
  const clipId = (index: number) => `puzzlio-${name}-clip-${index}`;

  return function SvgIcon({ className }) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox={spec.viewBox} className={cn(spec.web, className)}>
        <defs>
          {spec.shapes.map((shape, index) =>
            shape.clipPath ? (
              <clipPath key={index} id={clipId(index)}>
                <path d={shape.clipPath} />
              </clipPath>
            ) : null
          )}
        </defs>

        {spec.shapes.map((shape, index) => (
          <Shape key={index} shape={shape} clipId={shape.clipPath ? clipId(index) : undefined} />
        ))}
      </svg>
    );
  };
}

/** One shape, with its paint. */
function Shape({ shape, clipId }: { shape: IconShape; clipId?: string }) {
  const paint = {
    // An outline takes no fill; anything else inherits the svg's class when the artwork names none.
    fill: shape.fill ? colourFor(shape.fill) : isStroked(shape) ? 'none' : undefined,
    stroke: colourFor(shape.stroke),
    strokeWidth: shape.strokeWidth,
    strokeLinecap: shape.strokeLinecap,
    fillRule: shape.fillRule,
    clipRule: shape.clipRule,
    clipPath: clipId ? `url(#${clipId})` : undefined,
  };

  switch (shape.kind) {
    case 'path':
      return <path d={shape.d} {...paint} />;
    case 'rect':
      return <rect x={shape.x} y={shape.y} width={shape.width} height={shape.height} rx={shape.rx} {...paint} />;
    case 'circle':
      return <circle cx={shape.cx} cy={shape.cy} r={shape.r} {...paint} />;
  }
}

/**
 * Every icon in the spec, as a component. Derived rather than listed, so a new spec entry is drawn
 * here — and shown in the showcase — without anything else being edited.
 */
export const SVG_ICONS = Object.fromEntries(
  (Object.keys(ICONS) as IconName[]).map((name) => [name, makeSvgIcon(name)])
) as Record<IconName, SvgIcon>;

// Named exports, so callers import an icon rather than index a record.

export const UndoIcon = SVG_ICONS.undo;
export const LightbulbIcon = SVG_ICONS.lightbulb;
export const CheckIcon = SVG_ICONS.check;
export const HighlighterIcon = SVG_ICONS.highlighter;
export const PauseIcon = SVG_ICONS.pause;
export const PlayIcon = SVG_ICONS.play;
export const HelpIcon = SVG_ICONS.help;
export const SettingsIcon = SVG_ICONS.settings;
export const ArrowRightIcon = SVG_ICONS.arrowRight;
export const ArrowLeftIcon = SVG_ICONS.arrowLeft;

/** The archive's calendar. */
export const CalendarIcon = SVG_ICONS.calendar;

/** A filled padlock badge, for the highlighter's subscriber gate. */
export const LockBadgeIcon = SVG_ICONS.lock;

/** The week bar's day glyphs: a solved day's star, and the dots under a day in progress. */
export const CompleteStarIcon = SVG_ICONS.weekComplete;
export const IncompleteDotsIcon = SVG_ICONS.weekIncomplete;
/** The home cards' week strip, on a game's colour: a solved day, and the tile under a day in progress. */
export const WeekSolvedIcon = SVG_ICONS.weekSolved;
export const WeekInProgressIcon = SVG_ICONS.weekInProgress;

/** The outlined padlock, drawn in the caller's colour. */
export const LockOutlineIcon = SVG_ICONS.lockOutline;

/** The community links' marks, in the caller's colour. */
export const DiscordIcon = SVG_ICONS.discord;
export const FacebookIcon = SVG_ICONS.facebook;

/**
 * Downward chevron, as drawn for dropdowns. Rotate at the call site for other directions —
 * `-rotate-90` points it right, `rotate-90` left, `rotate-180` up.
 */
export const ChevronDownIcon = SVG_ICONS.chevronDown;

/** Google's own mark, required on a "Continue with Google" button by their branding terms. */
export const GoogleIcon = SVG_ICONS.googleG;

/** The brand mark, with or without the wordmark beside it. */
export const Logo: FC<{ className?: string; withName?: boolean }> = ({ className, withName = true }) => {
  const name = withName ? 'logoFull' : 'logo';
  const Mark = SVG_ICONS[name];
  const [, , width, height] = ICONS[name].viewBox.split(' ');
  return (
    // The artwork's own proportions, so a box sized on one side takes its other from them. Left to the
    // svg, WebKit widens a height-sized logo to fill the row and centres the artwork in it.
    <div className={cn('', className)} style={{ aspectRatio: `${width} / ${height}` }}>
      <Mark />
    </div>
  );
};
